import { motion } from 'framer-motion';
import { ArrowRight, Target, Eye, Heart, Award, Users, Clock, Shield } from 'lucide-react';

const WHATSAPP_NUMBER = '918880331224';

const values = [
  { icon: Award, title: 'Quality', description: 'We never compromise on the quality of materials and workmanship.' },
  { icon: Users, title: 'Client-Centric', description: 'Your satisfaction is our top priority at every step.' },
  { icon: Clock, title: 'Timely Delivery', description: 'We respect your time and deliver projects on schedule.' },
  { icon: Shield, title: 'Integrity', description: 'Transparent pricing and honest communication always.' },
];

const team = [
  { 
    name: 'Mohammed Akram', 
    role: 'Founder & CEO',
    image: 'https://images.pexels.com/photos/7937659/pexels-photo-7937659.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'
  },
  { 
    name: 'Suresh Patil', 
    role: 'Chief Architect',
    image: 'https://images.pexels.com/photos/3776969/pexels-photo-3776969.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'
  },
  { 
    name: 'Priya Reddy', 
    role: 'Interior Design Head',
    image: 'https://images.pexels.com/photos/4458210/pexels-photo-4458210.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'
  },
];

export default function About() {
  const handleConsultation = () => {
    const message = encodeURIComponent('Hello! I would like to learn more about Akruti Engineers & Builders.');
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, '_blank');
  };

  return (
    <div className="noise-overlay pt-24" data-testid="about-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-[#ececec]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="text-[#C9A646] text-xs uppercase tracking-[0.2em] font-bold">
              About Us
            </span>
            <h1 className="font-heading text-5xl md:text-7xl text-gray-900 mt-4">
              Building Excellence<br />
              <span className="text-gold-gradient">Since 2012</span>
            </h1>
            <p className="text-gray-600 text-lg mt-6 leading-relaxed">
              Akruti Engineers & Builders has been transforming dreams into reality for over 
              a decade. Based in Kalaburagi, Karnataka, we specialize in construction, 
              development, and interior design solutions that combine quality with affordability.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16 md:py-24 bg-[#f5f5f5]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <span className="text-[#C9A646] text-xs uppercase tracking-[0.2em] font-bold">
                Our Story
              </span>
              <h2 className="font-heading text-4xl md:text-5xl text-gray-900 mt-4">
                A Legacy of Trust & Quality
              </h2>
              <div className="space-y-4 mt-6 text-gray-600 leading-relaxed">
                <p>
                  Founded in 2012, Akruti Engineers & Builders started with a simple mission: 
                  to make quality construction accessible to everyone. What began as a small 
                  team of passionate builders has grown into one of the most trusted names in 
                  the region.
                </p>
                <p>
                  Over the years, we have completed over 150 projects, ranging from residential 
                  homes to commercial complexes and stunning interior designs. Our commitment 
                  to excellence, transparent pricing, and customer satisfaction has earned us 
                  the trust of more than 200 happy clients.
                </p>
                <p>
                  <strong className="text-[#C9A646]">We do interiors too!</strong> With many years of experience 
                  in interior design, we transform spaces into beautiful, functional environments 
                  that reflect your style and meet your needs.
                </p>
                <p>
                  Today, we continue to innovate and push boundaries, incorporating modern 
                  design principles and sustainable practices into every project we undertake.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_04b87573-a1ae-4a6c-8a55-99387c57d38e/artifacts/aiqn6ubz_depositphotos_327986174-stock-photo-two-storey-houses-construction-thailand.jpg"
                alt="Our Construction Work"
                className="w-full h-[500px] object-cover rounded-2xl shadow-xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-gradient-to-r from-[#C9A646] to-[#E6C36A] p-6 rounded-xl shadow-lg shadow-[#C9A646]/30">
                <span className="font-heading text-5xl text-[#0A0A0A]">12+</span>
                <p className="text-[#0A0A0A] text-sm font-semibold">Years of Excellence</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 md:py-24 bg-[#ececec]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="glass-card p-8 md:p-10"
            >
              <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-[#C9A646] to-[#E6C36A] mb-6 rounded-xl shadow-lg shadow-[#C9A646]/30">
                <Target size={28} className="text-[#0A0A0A]" />
              </div>
              <h3 className="font-heading text-3xl text-gray-900">Our Mission</h3>
              <p className="text-gray-600 mt-4 leading-relaxed">
                To deliver exceptional construction and interior design services that exceed 
                client expectations while maintaining affordability and the highest standards 
                of quality and craftsmanship.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="glass-card p-8 md:p-10"
            >
              <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-[#C9A646] to-[#E6C36A] mb-6 rounded-xl shadow-lg shadow-[#C9A646]/30">
                <Eye size={28} className="text-[#0A0A0A]" />
              </div>
              <h3 className="font-heading text-3xl text-gray-900">Our Vision</h3>
              <p className="text-gray-600 mt-4 leading-relaxed">
                To become the most trusted and innovative construction company in Karnataka, 
                known for transforming spaces and building lasting relationships with our 
                clients and communities.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 md:py-24 bg-[#f5f5f5]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#C9A646] text-xs uppercase tracking-[0.2em] font-bold">
              What Drives Us
            </span>
            <h2 className="font-heading text-4xl md:text-5xl text-gray-900 mt-4">
              Our Core Values
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center p-6 bg-white/50 border border-gray-200 hover:border-[#C9A646]/30 transition-all rounded-2xl shadow-lg shadow-gray-300/50 hover:shadow-[#C9A646]/10"
              >
                <div className="w-16 h-16 mx-auto flex items-center justify-center bg-gradient-to-br from-[#C9A646]/20 to-[#E6C36A]/20 mb-4 rounded-xl">
                  <value.icon size={32} className="text-[#C9A646]" />
                </div>
                <h3 className="font-heading text-xl text-gray-900">{value.title}</h3>
                <p className="text-gray-600 text-sm mt-2">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 md:py-24 bg-[#ececec]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#C9A646] text-xs uppercase tracking-[0.2em] font-bold">
              Meet Our Experts
            </span>
            <h2 className="font-heading text-4xl md:text-5xl text-gray-900 mt-4">
              Leadership Team
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                viewport={{ once: true }}
                className="group"
              >
                <div className="relative overflow-hidden">
                  <img 
                    src={member.image}
                    alt={member.name}
                    className="w-full h-80 object-cover grayscale group-hover:grayscale-0 transition-all duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#ececec] via-transparent to-white/0" />
                </div>
                <div className="mt-4 text-center">
                  <h3 className="font-heading text-2xl text-gray-900">{member.name}</h3>
                  <p className="text-[#C9A646] text-sm mt-1">{member.role}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-[#f5f5f5] border-t border-gray-200">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-4xl md:text-5xl text-gray-900">
              Let's Build Something<br />
              <span className="text-gold-gradient">Extraordinary Together</span>
            </h2>
            <p className="text-gray-600 text-lg mt-6 max-w-xl mx-auto">
              Ready to start your project? Get in touch with our team for a free consultation.
            </p>
            <button
              onClick={handleConsultation}
              className="mt-10 btn-primary inline-flex items-center gap-2"
              data-testid="about-cta-btn"
            >
              Start Your Project
              <ArrowRight size={18} />
            </button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
